import React, { useState } from 'react';
import { Box, Typography, TextField, Button, Paper, InputAdornment, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { enqueueSnackbar } from 'notistack';
import { useResetPasswordMutation } from './services/authenticationApi';
import CircularProgress from '@mui/material/CircularProgress';

const GradientBackground = styled(Box)(() => ({
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 50%, #3b82f6 100%)',
    clipPath: 'polygon(0 0, 51% 0, 39% 100%, 0% 100%)',
    '@media (max-width: 900px)': {
        clipPath: 'none'
    }
}));

interface ResetPasswordFormData {
    password: string;
    confirmPassword: string;
    ip: string;
}

const ResetPasswordPage = () => {
    const navigate = useNavigate();
    const [resetPassword, { isLoading }] = useResetPasswordMutation();
    const { register, handleSubmit, formState: { errors }, watch } = useForm<ResetPasswordFormData>();
    const [showPassword, setShowPassword] = useState(false);
    const [showNewPassword, setShowConfirmPassword] = useState(false);
    const [isFadingOut, setIsFadingOut] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleResetPassword = async (data: ResetPasswordFormData) => {
        setIsSubmitting(true);
        try {
            const response = await resetPassword({
                password: data.password,
                confirmPassword: data.confirmPassword,
                ip: "string" // You might want to get actual IP
            }).unwrap();

            if (response.success) {
                enqueueSnackbar(response.message, { variant: 'success' });
                setIsFadingOut(true);
                setTimeout(() => {
                    navigate('/login');
                }, 400); // 400ms fade süresi
            }
        } catch (error: any) {
            const status = error?.status ?? error?.originalStatus;
            if (status === 401) {
                navigate('/login', { replace: true });
                return;
            }
            // Diğer hatalar middleware tarafından gösterilecek
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Box className={isFadingOut ? 'fade-out' : ''} sx={{ position: 'relative', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <GradientBackground />

            <Paper
                component="form"
                onSubmit={handleSubmit(handleResetPassword)}
                elevation={3}
                sx={{
                    width: { xs: '80%', xl: '51.4%' },
                    maxWidth: '984px',
                    padding: { xs: 0, md: 5 },
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    bgcolor: 'rgba(255, 255, 255, 0.5)',
                    borderRadius: '35px',
                    border: '1.2px solid rgba(0, 0, 0, 0.1)',
                    position: 'relative',
                    zIndex: 9999,
                }}
            >
                <Box sx={{ width: { xs: '60%', md: '41%' }, p: 2.5, color: 'white' }}>
                    <Box
                        component="img"
                        src={`${process.env.PUBLIC_URL}/assets/images/logo/newlogo.png`}
                        alt="Logo"
                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                </Box>

                <Box sx={{
                    width: { xs: '100%', md: '59%' },
                    bgcolor: { xs: 'white', md: 'transparent' },
                    p: { xs: 2.5, md: 0 },
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 3,
                    zIndex: 5000,
                    borderRadius: "0 0 35px 35px !important",
                }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, fontSize: { xs: '16px', md: '22px' } }}>
                        Şifre Sıfırlama
                    </Typography>

                    <Box sx={{ width: '80%' }}>
                        <TextField
                            fullWidth
                            label="Şifre"
                            type={showPassword ? 'text' : 'password'}
                            {...register('password', {
                                required: 'Şifre gerekli',
                                minLength: {
                                    value: 6,
                                    message: 'Şifre en az 6 karakter olmalıdır'
                                }
                            })}
                            error={!!errors.password}
                            helperText={errors.password?.message}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                            sx={{ mb: 2 }}
                        />

                        <TextField
                            fullWidth
                            label="Şifre Tekrar"
                            type={showNewPassword ? 'text' : 'password'}
                            {...register('confirmPassword', {
                                required: 'Şifre tekrarı gerekli',
                                validate: (val: string) => {
                                    if (watch('password') !== val) {
                                        return "Şifreler eşleşmiyor";
                                    }
                                }
                            })}
                            error={!!errors.confirmPassword}
                            helperText={errors.confirmPassword?.message}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowConfirmPassword(!showNewPassword)} edge="end">
                                            {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                            sx={{ mb: 3 }}
                        />

                        <Button
                            type="submit"
                            fullWidth
                            disabled={isSubmitting || isLoading}
                            sx={{
                                height: '52px',
                                background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                                borderColor: '#1e3c72',
                                borderRadius: '12px',
                                color: 'white',
                                fontSize: '15px',
                                fontWeight: 600,
                                textTransform: 'none',
                                boxShadow: '0 4px 16px rgba(30, 60, 114, 0.25)',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #1a3569 0%, #25478a 100%)',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 8px 24px rgba(30, 60, 114, 0.35)'
                                },
                                '&:disabled': {
                                    background: 'rgba(30, 60, 114, 0.3)',
                                    color: 'rgba(255, 255, 255, 0.6)'
                                },
                                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                            }}
                        >
                            {isSubmitting || isLoading ? <CircularProgress size={24} color="inherit" /> : 'Gönder'}
                        </Button>
                    </Box>
                </Box>
            </Paper>

            <style>{`
                .fade-out {
                    animation: fadeOutResetPassword 0.4s forwards;
                }
                @keyframes fadeOutResetPassword {
                    from { opacity: 1; }
                    to { opacity: 0; }
                }
            `}</style>
        </Box>
    );
};

export default ResetPasswordPage;
